import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  getStatusStyles,
  PERMISSIONS,
  STATIC_KEYS,
} from "src/constants/constants";
import { AlertService } from "src/services/alert.service";
import { CommonService } from "src/services/common.service";

export interface TableColumnList {
  name: string;
  key: string;
  width: string;
  type:
    | "text"
    | "image"
    | "toggle"
    | "input"
    | "dropdown"
    | "link"
    | "iconButton"
    | "multipleTexts"
    | "status"
    | "iconButton2";
  image?: boolean;
  event?: string;
  style?: Record<string, string>;
  spanStyle?: Record<string, string>;
  iconUrl?: string;
  buttonIconUrl?: string;
  avatarKey?: string;
  iconHeight?: string;
  iconWidth?: string;
  noFilterIcon?: boolean;
  showAfterText?: boolean;
  isStatus?: boolean;
}

@Component({
  selector: "table-component",
  templateUrl: "./table.component.html",
  styleUrls: ["./table.component.scss"],
})
export class TableComponent implements OnInit {
  @Input() tableData: any[] = [];
  @Input() columnList: TableColumnList[] = [];
  @Input() isDragDisabled: boolean = true;
  @Input() menuFlag: boolean = false;
  @Input() menuOptions: {
    key: string;
    event: string;
  }[] = [];
  @Input() isLoading: boolean = true;
  @Input() isDialogContent?: boolean = false;
  @Input() getCustomMenuOptions?: (event: any) => any[];
  @Input() enableRowSelection: boolean = false;
  @Input() bulkActions: {
    key: string;
    event: string;
  }[] = [];
  @Input() isBulkActionAllowed?: (action: string, selectedRows: any[]) => boolean;
  @Input() isRowSelectable?: (item: any) => boolean;
  @Output() actionEvent: EventEmitter<{ action: string; item: any }> =
    new EventEmitter();
  @Output() toggleEvent: EventEmitter<{ event: any; item: any }> =
    new EventEmitter();
  @Output() orderChangeEvent: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() onIconurlclick = new EventEmitter();
  @Output() selectedRowsChange = new EventEmitter<any[]>();
  @Output() bulkActionEvent = new EventEmitter<{
    action: string;
    items: any[];
  }>();
  @Input() selectedRows: any[] = [];

  skeletonRows: number = 5;
  statusStyle = getStatusStyles;
  userInfo: any;
  userRole: string = "";
  constructor(
    private alertService: AlertService,
    private commonService: CommonService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.getLoggedInInformation();
  }

  getLoggedInInformation() {
    this.userInfo = this.commonService.getItem(
      STATIC_KEYS.LOGGED_IN_USER_INFO,
      true
    );
  }

  onAction(action: string, item: any): void {
    this.actionEvent.emit({ action, item });
  }

  onToggleChange(event: any, item: any): void {
    this.toggleEvent.emit({ event, item });
  }

  getMergedStyle(item: any, column: any): any {
    const baseStyle = column?.spanStyle || {};

    if (column?.key === "reviewStatus" && item?.reviewStatus) {
      return {
        padding: "4px 8px",
        borderRadius: "8px",
        fontWeight: 600,
        ...this.statusStyle(item.reviewStatus),
        ...baseStyle,
      };
    }
    // console.log('ITem', item.status, column.key);
    if(column?.key === 'status'){
      return {
        fontWeight: 600,
        ...this.statusStyle(item.status),
        ...baseStyle,
      };
    }

    return baseStyle;
  }
  copyToClipboard(value: string): void {
    // Create a temporary input element to copy the text
    const textArea = document.createElement("textarea");
    textArea.value = value;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand("copy");
    document.body.removeChild(textArea);
    this.alertService.showSnackBar("Copied!");
  }

  handleBrokenImage(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = "assets/images/no-image-preview.png";
  }

  drop(event: CdkDragDrop<any[]>): void {
    moveItemInArray(this.tableData, event.previousIndex, event.currentIndex);
    this.orderChangeEvent.emit(this.tableData);
  }

  getMenuOptions(event: any) {
    const { identifierName = [], permissions: requiredPermissions = [] } =
      this.route.snapshot.data;
    let option: any = [];
    if (this?.getCustomMenuOptions) {
      const dd = this.getCustomMenuOptions(event) || [];
      option = [...dd];
    } else {
      option = [...this.menuOptions];
    }

    if (requiredPermissions.length) {
      option = [
        ...this.getPermissionOptions(
          requiredPermissions,
          identifierName,
          option
        ),
      ];
      // this.getPermissionOptions(requiredPermissions, identifierName, option);
    }

    return option;
  }

  getPermissionOptions(
    requiredPermissions: string[],
    identifierName: string[] = [],
    options: any[] = []
  ): any[] {
    // 1. Sanitize input: ensure it's an array and remove null/undefined items immediately
    let permissionOption: any[] = Array.isArray(options)
      ? options.filter(Boolean)
      : [];

    const userPermissions = this.commonService.getPermissions();

    if (PERMISSIONS.ALL in userPermissions || "r-all" in userPermissions) {
      return permissionOption;
    }

    if (identifierName?.length) {
      identifierName.forEach((identName) => {
        const grantedActions = userPermissions[identName] || [];

        // If the user has some specific actions but not 'all'
        if (grantedActions?.length && !grantedActions.includes("all")) {
          permissionOption = permissionOption.filter((option) => {
            // Extra safety check for the option itself
            if (!option) return false;

            // If no permission key is required for the menu item, show it;
            // otherwise check if user has the specific permission
            return option.permission
              ? grantedActions.includes(option.permission)
              : true;
          });
        }
      });
    }

    // Final pass to ensure no nulls were introduced during filtering logic
    return permissionOption.filter(Boolean);
  }

  onImageAvtrError(event: Event) {
    const imgElement = event.target as HTMLImageElement;
    imgElement.src = "assets/images/avatar.jpg";
  }

  trackFn(item: { value: string; key: string }) {
    return item.key;
  }

  getStatusMergedStyle(item: any, column: any): any {
    const baseStyle = column?.spanStyle || {};

    if (column?.isStatus && item?.[column.key]) {
      return {
        padding: "4px 8px",
        borderRadius: "8px",
        fontWeight: 600,
        ...this.statusStyle(item[column.key]),
        ...baseStyle,
      };
    }

    return baseStyle;
  }

isSelectable(item: any): boolean {
  return this.isRowSelectable ? this.isRowSelectable(item) : true;
}

isRowSelected(item: any): boolean {
  return this.selectedRows.includes(item);
}

toggleRowSelection(item: any, event: Event): void {
  event.stopPropagation();

  if (!this.isSelectable(item)) {
    return;
  }

  const checked = (event.target as HTMLInputElement).checked;

  if (checked) {
    if (!this.selectedRows.includes(item)) {
      this.selectedRows = [...this.selectedRows, item];
    }
  } else {
    this.selectedRows = this.selectedRows.filter(
      (selectedItem) => selectedItem !== item
    );
  }

  this.selectedRowsChange.emit(this.selectedRows);
}

toggleSelectAll(event: Event): void {
  const checked = (event.target as HTMLInputElement).checked;
  const selectableRows = this.tableData.filter((item) =>
    this.isSelectable(item)
  );

  if (checked) {
    this.selectedRows = [...selectableRows];
  } else {
    this.selectedRows = [];
  }

  this.selectedRowsChange.emit(this.selectedRows);
}

isAllSelected(): boolean {
  const selectableRows = this.tableData.filter((item) =>
    this.isSelectable(item)
  );

  return (
    selectableRows.length > 0 &&
    selectableRows.every((item) => this.selectedRows.includes(item))
  );
}

clearSelection(): void {
  this.selectedRows = [];
  this.selectedRowsChange.emit(this.selectedRows);
}

isActionAllowed(action: string): boolean {
  return (
    this.selectedRows.length > 0 &&
    (this.isBulkActionAllowed
      ? this.isBulkActionAllowed(action, this.selectedRows)
      : true)
  );
}

onBulkAction(action: string): void {
  if (!this.isActionAllowed(action)) {
    return;
  }
  this.bulkActionEvent.emit({ action, items: [...this.selectedRows] });
}
}
